import React, { useState, useMemo, useEffect } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import CustomerDropDown from '../../components/shared/CustomerDropDown'
import HotelsDropDown from '../../components/shared/HotelsDropDown';
import RatesDropDown from '../../components/shared/RatesDropDown'
import RoomTypesDropDown from '../../components/shared/RoomTypesDropDown';
import { useMessageResources, useTextResources } from '../../language';
import { Form } from 'semantic-ui-react';
import GridSearchTemplate from '../../pages/GridSearchTemplate';
import { AllocationAndStrategy, Promotions, RoomTypes } from '../../api/services/next'
import CheckboxWrapper from '../../components/shared/Checkbox';
import { groupByIntoArray } from '../../components/shared/utils';
import { onError, onSuccess } from '../../components/shared/messages';
import { AllocationCreationPopup } from '../../components/AllocationAndStrategy/AllocationCreationPopup';
import { AllocationDatesPopup } from '../../components/AllocationAndStrategy/AllocationDatesPopup';
import { useConfirm } from '../../hooks/useConfirm';
import { Popup } from 'semantic-ui-react'
import { AllocationTypes, AllocationTypesEnum, InventoryReflectionTypes, InventoryReflectionTypesEnum } from "../../components/shared/constants";
import { EditButton, IconButton, TrashButton } from '../../components/shared/icons';
import { useCurrentHotel } from '../../hooks/useCurrentHotel';
import { DatePicker } from '../../components/shared/DatePicker';
import './style.css';
import AllocationsDropDown from '../../components/shared/AllocationsDropDown';
import DropDown from '../../components/shared/dropdown';
import { AllocationDateRangesPopup } from '../../components/AllocationAndStrategy/AllocationDateRangesPopup';
import { useLoader } from '../../hooks/useLoader';
import PromotionsDropDown from '../../components/shared/PromotionsDropDown';

// Distinguishes "never touched" (null) from an explicit 0 for AllocationDate rows -
// exported (rather than kept as a closure inside Allocation) so it's unit-testable in isolation.
export const numericFormatter = ({ value, row }) => {
    if (row.AllocationDateId) {
        return value ?? "";
    }
    return value || "N/A";
}

// Formatter for AllocatedRooms - shows red if auto-released. Distinguishes "never touched" (null,
// e.g. a Maximum/TopUp-only row) from an explicit 0, same as numericFormatter above.
export const allocatedRoomsFormatter = ({ value, row }) => {
    if (row.AllocationDateId) {
        const isAutoReleased = row.IsAutoReleased === true;
        const displayValue = value ?? "";
        if (isAutoReleased && displayValue === 0) {
            return <span style={{ color: 'red', fontWeight: 'bold' }}>{displayValue}</span>;
        }
        return displayValue;
    }
    return value || "N/A";
}

const Allocation = ({ HotelId = useCurrentHotel()?.Id }) => {
    const confirm = useConfirm();
    const text = useTextResources();
    const message = useMessageResources();
    const loader = useLoader();
    const [allocationDatesData, setAllocationDatesData] = useState([]);
    const [allocationsData, setAllocationsData] = useState([]);
    const [hotelIds, setHotelIds] = useState(HotelId ? [HotelId] : []);
    const [roomTypesData, setRoomTypesData] = useState([]);
    const [allocationsDropdownData, setAllocationsDropdownData] = useState([])
    const [spec, setSpec] = useState({});
    const [showDatesPopup, setShowDatesPopup] = useState(false);
    const [showAllocationDateRangesPopup, setShowAllocationDateRangesPopup] = useState(false);
    const [selectedAllocation, setSelectedAllocation] = useState({});
    const [updateAllocationPopupOpen, setUpdateAllocationPopupOpen] = useState(false);

    const [selectedAllocationType, setSelectedAllocationType] = useState(0);

    const createAllocationDropDownData = useMemo(() => [
        { Id: 0, Name: text("New") },
        { Id: AllocationTypesEnum.PerRoomNights, Name: text(AllocationTypes[AllocationTypesEnum.PerRoomNights]) },
        { Id: AllocationTypesEnum.PerReservation, Name: text(AllocationTypes[AllocationTypesEnum.PerReservation]) }
    ], [text]);

    const RatesSpec = useMemo(() => ({
        HotelIds: hotelIds,
        IncludeDayUse: true,
        IncludeRatePricingType: true,
        IsActive: true,
    }), [hotelIds]);

    const PromotionsSpec = useMemo(() => ({
        HotelIds: hotelIds,
        RateIds: spec.RateIds,
    }), [hotelIds, spec.RateIds]);

    useEffect(() => {
        (async () => {
            const roomtypes = await RoomTypes.GetRoomTypes({ HotelIds: hotelIds });
            setRoomTypesData(roomtypes);
        })()
    }, [hotelIds])

    const allocationsDropDownSpec = useMemo(() => ({
        HotelIds: spec.HotelIds,
        RoomTypeIds: spec.RoomTypeIds,
        RateIds: spec.RateIds,
        PromotionIds: spec.PromotionIds,
        CustomerIds: spec.CustomerIds,
        InActive: spec.InActive,
        IncludeHotelLevelWhenSuppliedRoomTypeIds: true,
        SkipCache: true,
    }), [spec.HotelIds, spec.RoomTypeIds, spec.RateIds, spec.CustomerIds, spec.InActive]);

    useEffect(() => {
        (async () => {
            if (allocationsDropDownSpec.HotelIds?.length) {
                const allocations = await AllocationAndStrategy.GetAllocations(allocationsDropDownSpec);
                const allocationsData = groupByIntoArray(allocations, 'Id').map((a) => ({ Id: a?.[0]?.Id, Name: a?.[0]?.Name }));
                setAllocationsDropdownData(allocationsData);
            }
            else {
                setAllocationsDropdownData([]);
            }
        })()
    }, [allocationsDropDownSpec])

    const formatter = ({ value }) => {
        return value || "N/A";
    }

    // Formatter for ReleaseDate - calculate at runtime from AllocationDate - ReleaseDays
    const releaseDateFormatter = ({ row }) => {
        if (!row.ReleaseDays || !row.AllocationDate) return '';
        return moment.parseZone(row.AllocationDate).subtract(row.ReleaseDays, 'days').format('DD/MM/YYYY');
    }

    const addRow = () => {
        return executeAllocationAction(undefined, undefined, true);
    }

    const executeAllocationAction = async (callback, dataArr, refreshData, isDelete) => {
        try {
            loader(true)
            if (callback) {
                await callback(dataArr);
                onSuccess(isDelete ? "DeletedSuccessfully" : null);
            }
            if (refreshData) {
                var res = await AllocationAndStrategy.GetAllocations(spec);
                res = res.map(a => ({ ...a, Actions: a.Id }))
                setAllocationsData(res);
                setAllocationDatesData(mapResponseData(res));
            }
        } catch (error) {
            onError(message(error));
        } finally {
            loader(false);
        }
    }

    const onSearchChange = (newSpec) => {
        setSpec(newSpec)
    }

    const idFormatter = ({ value }) => {
        const selectedAllocation = value && allocationDatesData.length && allocationDatesData.find(a => a.Id == value);
        return selectedAllocation ? <span>
            <Popup content={formatNameTooltip(selectedAllocation)} trigger={<span className={selectedAllocation.IsActive ? "" : "inActive"}>{selectedAllocation?.Name} </span>} />
        </span> : ""
    };

    const ExtRoomTypeFormatter = ({ value }) => {
        if (value === "") {
            return "";
        }

        return value || "N/A";
    }

    const extractDateValue = (value, format) => {
        return value && moment.parseZone(value).format(format);
    }

    const actionsFormatter = ({ value, row }) => {
        const selectedAllocation = allocationsData.length && allocationsData.filter(a => a.Id == value)
            .reduce((accumulator, allocationInstance) => ({
                ...accumulator,
                ...allocationInstance,
                CustomerIds: !allocationInstance.CustomerGroupId ? [...accumulator.CustomerIds, allocationInstance.CustomerId] : accumulator.CustomerIds,
                RoomTypeIds: [...accumulator.RoomTypeIds, allocationInstance.RoomTypeId],
                RateIds: [...accumulator.RateIds, allocationInstance.RateId],
                PromotionIds: allocationInstance.PromotionId ? [...accumulator.PromotionIds, allocationInstance.PromotionId] : accumulator.PromotionIds,
                CustomerGroupIds: allocationInstance.CustomerGroupId ? [...accumulator.CustomerGroupIds, allocationInstance.CustomerGroupId] : accumulator.CustomerGroupIds,
            }), {
                CustomerIds: [],
                RoomTypeIds: [],
                RateIds: [],
                PromotionIds: [],
                CustomerGroupIds: [],
            });
        if (selectedAllocation.CustomerIds?.length) {
            selectedAllocation.CustomerIds = [...new Set(selectedAllocation.CustomerIds)];
        }
        if (selectedAllocation.RoomTypeIds?.length) {
            selectedAllocation.RoomTypeIds = [...new Set(selectedAllocation.RoomTypeIds)];
        }
        if (selectedAllocation.RateIds?.length) {
            selectedAllocation.RateIds = [...new Set(selectedAllocation.RateIds)];
        }
        if (selectedAllocation.PromotionIds?.length) {
            selectedAllocation.PromotionIds = [...new Set(selectedAllocation.PromotionIds)];
        }
        if (selectedAllocation.CustomerGroupIds?.length) {
            selectedAllocation.CustomerGroupIds = [...new Set(selectedAllocation.CustomerGroupIds)];
        }
        if (row || !selectedAllocation?.IsActive) {
            return " ";
        }
        else {
            return <>
                <TrashButton disabled={selectedAllocation.IsStrategy} content={"Delete"} onClick={() => { deleteLinkClicked(value) }} style={{ marginRight: '1rem' }} />
                <EditButton disabled={selectedAllocation.IsStrategy} content={"EditAllocation"} onClick={() => {
                    setSelectedAllocation(selectedAllocation);
                    setUpdateAllocationPopupOpen(true);
                }} style={{ marginRight: '1rem' }} />
                <IconButton icon={'calendar alternate outline'} disabled={selectedAllocation.IsStrategy} content={"AddAllocationDates"} onClick={() => openDatesPopup(selectedAllocation)} />
            </>
        }
    };


    const formatNameTooltip = (allocation) => {
        const id = `${text('Identifier')}: ${allocation.Id}`;
        const reflection = `${text('InventoryReflection')}: ${text(InventoryReflectionTypes.find(x => x.Id == allocation.InventoryReflectionId)?.Name)}`;
        const considerOverbooking = allocation.ConsiderOverbooking ? `${text('ConsiderOverbooking')}` : '';
        const distribute = allocation.Distribute ? `${text('DistributeAllocations')}` : '';
        const isSharedInventory = allocation.IsSharedInventory ? `${text('SharedInventory')}` : '';
        const allocationType = text(AllocationTypes[allocation.IsPerReservation ? AllocationTypesEnum.PerReservation : AllocationTypesEnum.PerRoomNights]);

        return (
            <p style={{ padding: '5px' }}>
                <div>{id}</div>
                <div>{allocationType}</div>
                <div>{reflection}</div>
                {considerOverbooking && <div>{considerOverbooking}</div>}
                {distribute && <div>{distribute}</div>}
                {isSharedInventory && !allocation.IsPerReservation && <div>{isSharedInventory}</div>}
            </p>
        );
    }

    const toolTipFormatter = (value) => {
        return <Popup content={value} trigger={<span>{value}</span>} />
    }

    const deleteLinkClicked = async (id) => {
        await confirm({
            header: `${text('Delete')} ${text('Allocation')}`,
            confirmButton: {
                content: 'Yes',
                positive: true,
            },
            cancelButton: {
                content: 'No',
                negative: true,
            },
            onConfirm: async () => {
                await executeAllocationAction(AllocationAndStrategy.DeleteAllocations, [id], true, true);
            }
        });
    }

    const selectedAllocationTypeChanged = (value) => {
        setSelectedAllocationType(value);
        if (value !== 0) {
            if (value === AllocationTypesEnum.PerReservation) {
                setSelectedAllocation({
                    IsPerReservation: true,
                    InventoryReflectionId: InventoryReflectionTypesEnum.NoInventoryReflection,
                    IsSharedInventory: true,
                });
            }
            else if (value === AllocationTypesEnum.PerRoomNights) {
                setSelectedAllocation({
                })
            }

            setUpdateAllocationPopupOpen(true);
        }
    }

    const openDatesPopup = (allocation) => {
        setSelectedAllocation(allocation);
        if (allocation.IsPerReservation) {
            setShowAllocationDateRangesPopup(true);
        }
        else {
            setShowDatesPopup(true);
        }
        return true;
    }

    const removeDuplicates = (array, keys) => {
        if (!Array.isArray(keys)) {
            keys = [keys];
        }

        const uniqueObjects = new Map();

        array.forEach(obj => {
            const key = keys.map(k => obj[k]).join('|');
            if (!uniqueObjects.has(key)) {
                uniqueObjects.set(key, obj);
            }
        });

        return Array.from(uniqueObjects.values());
    }

    const mapResponseData = (data) => {
        for (let item of data) {
            item.Actions = item.Id;
        }

        const flattenData = data.flatMap(allocation =>
            allocation.AllocationDates.length &&
                (allocation.IsPerReservation || allocation.HotelLevelAllocation || allocation.AllocationDates.find(a => a.ExtRoomType?.includes(allocation.RoomTypeName)))
                ?
                allocation.AllocationDates.map(date => ({
                    Id: allocation.Id,
                    Name: allocation.Name,
                    InventoryReflectionId: allocation.InventoryReflectionId,
                    IsActive: allocation.IsActive,
                    Distribute: allocation.Distribute,
                    ConsiderOverbooking: allocation.ConsiderOverbooking,
                    CreatedDate: allocation.CreatedDate,
                    IsSharedInventory: allocation.IsSharedInventory,
                    IsPerReservation: allocation.IsPerReservation,
                    IsStrategy: allocation.IsStrategy,
                    HotelId: allocation.HotelId,
                    HotelName: allocation.HotelName,
                    WingId: allocation.WingId,
                    WingName: allocation.WingName,
                    HotelLevelAllocation: allocation.HotelLevelAllocation,
                    RoomTypeId: allocation.RoomTypeId,
                    RoomTypeName: allocation.RoomTypeName,
                    CustomerId: allocation.CustomerId,
                    CustomerName: allocation.CustomerName,
                    RateId: allocation.RateId,
                    RateName: allocation.RateName,
                    MinDate: allocation.MinDate,
                    MaxDate: allocation.MaxDate,
                    AllocationDateId: date.Id,
                    AllocationDate: date.Date,
                    ExtRoomType: date.ExtRoomType || allocation.RoomTypeName,
                    AllocatedRooms: date.AllocatedRooms,
                    TopUpRooms: date.TopUpRooms,
                    MaximumRooms: date.MaximumRooms,
                    ExcludedRooms: date.ExcludedRooms,
                    MinimumForInventoryReflection: date.MinimumForInventoryReflection,
                    ReservedRooms: date.ReservedRooms,
                    LeftRooms: date.LeftRooms,
                    ReleaseDays: date.ReleaseDays,
                    IsAutoReleased: date.IsAutoReleased,
                    Actions: allocation.Id,
                }))
                :
                [{
                    Id: allocation.Id,
                    Name: allocation.Name,
                    InventoryReflectionId: allocation.InventoryReflectionId,
                    IsActive: allocation.IsActive,
                    Distribute: allocation.Distribute,
                    ConsiderOverbooking: allocation.ConsiderOverbooking,
                    CreatedDate: allocation.CreatedDate,
                    IsSharedInventory: allocation.IsSharedInventory,
                    IsPerReservation: allocation.IsPerReservation,
                    IsStrategy: allocation.IsStrategy,
                    HotelId: allocation.HotelId,
                    HotelName: allocation.HotelName,
                    WingId: allocation.WingId,
                    WingName: allocation.WingName,
                    HotelLevelAllocation: allocation.HotelLevelAllocation,
                    ExtRoomType: allocation.RoomTypeName,
                    RoomTypeId: allocation.RoomTypeId,
                    RoomTypeName: allocation.RoomTypeName,
                    CustomerId: allocation.CustomerId,
                    CustomerName: allocation.CustomerName,
                    RateId: allocation.RateId,
                    RateName: allocation.RateName,
                    MinDate: allocation.MinDate,
                    MaxDate: allocation.MaxDate,
                    Actions: allocation.Id,
                }]
        );

        // The backend only attaches AllocationDates to one combination row per RoomType (to avoid sending
        // that array once per Rate/Customer combination). Every other combination row for the same
        // allocation therefore falls into the dateless branch above; drop those once a dated row for the same
        // allocation exists, otherwise they'd show up as spurious "N/A" rows alongside the real date rows.
        const idsWithDateRows = new Set(flattenData.filter(row => row.AllocationDateId != null).map(row => row.Id));
        const dedupedFlattenData = flattenData.filter(row => row.AllocationDateId != null || !idsWithDateRows.has(row.Id));

        const sortedData = dedupedFlattenData.sort((a, b) => new Date(b.AllocationDate || b.CreatedDate) - new Date(a.AllocationDate || a.CreatedDate));

        return removeDuplicates(sortedData, ['Id', 'ExtRoomType', 'AllocationDateId']);
    };


    const onSearchComplete = (data) => {
        const flattenData = mapResponseData(data);
        setAllocationsData(data);
        setAllocationDatesData(flattenData);
    }

    const columns = [
        { name: 'Id', title: text('Allocation'), formatter: idFormatter },
        {
            name: 'Day', title: text('Day'), formatter: numericFormatter,
            getCellValue: row => (row.AllocationDate ? extractDateValue(row.AllocationDate, 'D') : undefined)
        },
        {
            name: 'Month', title: text('Month'), formatter: formatter,
            getCellValue: row => (row.AllocationDate ? extractDateValue(row.AllocationDate, 'MMMM') : undefined)
        },
        {
            name: 'Year', title: text('Year'), formatter: formatter,
            getCellValue: row => (row.AllocationDate ? extractDateValue(row.AllocationDate, 'YYYY') : undefined)
        },
        {
            name: 'ExtRoomType', title: text('RoomType'),
            formatter: ExtRoomTypeFormatter,
            getCellValue: row => (row.HotelLevelAllocation ? "" : row.ExtRoomType)
        },
        { name: 'AllocatedRooms', title: text('NumberAllocatedRooms'), formatter: allocatedRoomsFormatter },
        { name: 'TopUpRooms', title: text('TopUp'), formatter: numericFormatter },
        { name: 'MaximumRooms', title: text('MaximumRooms'), formatter: numericFormatter },
        { name: 'ExcludedRooms', title: text('RoomsToKeep'), formatter: numericFormatter },
        { name: 'MinimumForInventoryReflection', title: text('ReflectFromMinimumRooms'), formatter: numericFormatter },
        { name: 'ReservedRooms', title: text('UsedRooms'), formatter: numericFormatter },
        { name: 'LeftRooms', title: text('LeftRooms'), formatter: numericFormatter },
        { name: 'ReleaseDays', title: text('ReleaseDays'), formatter: numericFormatter },
        { name: 'ReleaseDate', title: text('ReleaseDate'), formatter: releaseDateFormatter },
        { name: 'Actions', title: text('Actions'), formatter: actionsFormatter },

        {
            name: 'hiddenAllocationId', title: 'hiddenAllocationId',
            getCellValue: row => row.Id
        },
        {
            name: 'hiddenAllocationDate', title: 'hiddenAllocationDate',
            getCellValue: row => (row.AllocationDate ? new Date(row.AllocationDate).getTime() : undefined)
        }
    ].map(c => ({ ...c, title: toolTipFormatter(c.title) }));

    const gridOptions = {
        search: {
            fields: [
                <Form.Field
                    control={HotelsDropDown}
                    multiple
                    clearable
                    search
                    label={text("Hotels")}
                    key="HotelIds"
                    onChange={setHotelIds}
                />,
                <Form.Field
                    control={RoomTypesDropDown}
                    multiple
                    clearable
                    search
                    label={text("RoomTypes")}
                    key="RoomTypeIds"
                    data={roomTypesData}
                />,
                <Form.Field
                    control={CustomerDropDown}
                    multiple
                    clearable
                    search
                    label={text("Customers")}
                    key="CustomerIds"
                />,
                <Form.Field
                    control={RatesDropDown}
                    multiple
                    clearable
                    search
                    spec={RatesSpec}
                    label={text("Rates")}
                    key="RateIds"
                />,
                <Form.Field
                    control={PromotionsDropDown}
                    multiple
                    clearable
                    search
                    spec={PromotionsSpec}
                    label={text("Promotions")}
                    key="PromotionIds"
                />,
                <Form.Field
                    control={AllocationsDropDown}
                    multiple
                    clearable
                    search
                    data={allocationsDropdownData}
                    label={text("Allocations")}
                    key="Ids"
                />,
                <Form.Field key='Empty1' />,
                <Form.Field key='Empty2' />,
                    <DatePicker clearable label={text('StartDate')} key='FromDate' />,
                    <DatePicker clearable label={text('EndDate')} key='ToDate' />,
                    <CheckboxWrapper
                        key="InActive"
                        label={text("IncludeInactive")}
                    />,
                    <CheckboxWrapper
                        key="IncludeAllocationsWithoutDates"
                        label={text("IncludeAllocationsWithoutDates")}
                    />

            ],

            method: 'POST',
            api: AllocationAndStrategy.GetAllocations,
            searchOnLoad: true,
            initial: {
                HotelIds: hotelIds.length ? hotelIds : null,
                InActive: false,
                IncludeHotelLevelWhenSuppliedRoomTypeIds: true,
                IncludeAllocationsWithoutDates: true,
                FromDate: new Date(),
                ToDate: moment().add(1, 'years').toDate(),
                SkipCache: true,
            },
        },
        grid: {
            columns: columns,
            cacheId: `Allocation`,
            customOptions: {
                sort: {
                    sorting: [
                        { columnName: 'hiddenAllocationId', direction: 'desc' },
                        { columnName: 'hiddenAllocationDate', direction: 'asc' },
                    ],
                },
                group: {
                    grouping: [{ columnName: 'Id' }, { columnName: 'ExtRoomType' }, { columnName: 'Year' }, { columnName: 'Month' }],
                    extensions: [
                        { columnName: 'Id', groupingEnabled: false },
                    ]
                },
                hidden: {
                    hiddenColumns: [
                        { columnName: 'hiddenAllocationId' },
                        { columnName: 'hiddenAllocationDate' },
                    ],
                },
            },
            filtering: {
                extensions: [
                    { columnName: 'Actions', filteringEnabled: false }
                ]
            },
            showFilter: true,
            summary: true,
            groupSummaryItems: [
                { columnName: 'Actions', type: 'max', alignByColumn: true, formatter: actionsFormatter, hideTypeName: true },
            ],
            noVirtualTable: true,
            paging: false,
            menu: [(
                <DropDown
                    key="allocationCreationPopup"
                    data={createAllocationDropDownData}
                    value={selectedAllocationType}
                    onChange={selectedAllocationTypeChanged}
                    textField="Name"
                    valueField="Id"
                    style={{ width: '20em' }}
                />
            )],
            rowClassCallback: a => !a.IsActive ? "inActive" : "",
            editable: {
                modalEditor: {
                    modal: AllocationCreationPopup,
                    props: {
                        addRow: addRow
                    }
                }
            }
        },
    };
    return (
        <div className="gridSearchTemplate allocation-grid-search">
            <GridSearchTemplate options={gridOptions} onSearchChange={onSearchChange} data={allocationDatesData} onSearchComplete={onSearchComplete} />
            <AllocationDatesPopup
                open={showDatesPopup}
                onClose={() => { executeAllocationAction(undefined, undefined, true); setShowDatesPopup(false) }}
                selectedAllocation={selectedAllocation} />
            <AllocationCreationPopup extendedProps={{ addRow }} updatedAllocation={selectedAllocation} open={updateAllocationPopupOpen}
                onCancelChanges={() => { setUpdateAllocationPopupOpen(false); setSelectedAllocationType(0); }}
                onApplyChanges={() => setUpdateAllocationPopupOpen(false)} />
            <AllocationDateRangesPopup selectedAllocation={selectedAllocation} open={showAllocationDateRangesPopup}
                onClose={() => {
                    executeAllocationAction(undefined, undefined, true);
                    setShowAllocationDateRangesPopup(false)
                }} />

        </div>
    )
};

Allocation.propTypes = {
    HotelId: PropTypes.any
};

export default Allocation;
